import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Percent, Package, Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { percentageTotal, uid } from "@/components/wizard/validation";

export default function DistributionStep({
  distribution,
  setDistribution,
  family,
  assets,
}) {
  const mode = distribution?.mode;

  const setMode = (m) => {
    // seed default state for the chosen mode
    if (m === "percentage") {
      const seeded =
        distribution.percentages && distribution.percentages.length > 0
          ? distribution.percentages
          : family.map((f) => ({
              id: uid(),
              beneficiary_name: f.name || "",
              beneficiary_relationship: f.relationship || "",
              share: "",
            }));
      setDistribution({
        mode: "percentage",
        percentages: seeded,
        asset_assignments: distribution.asset_assignments || [],
      });
    } else {
      const seeded =
        distribution.asset_assignments && distribution.asset_assignments.length === assets.length
          ? distribution.asset_assignments
          : assets.map((a) => ({
              asset_id: a.id,
              asset_description: a.description || a.type,
              beneficiaries: [],
              notes: "",
            }));
      setDistribution({
        mode: "asset_wise",
        percentages: distribution.percentages || [],
        asset_assignments: seeded,
      });
    }
  };

  return (
    <div data-testid="step-distribution">
      <h2 className="font-serif text-2xl text-navy">Distribution of Estate</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        Choose how you want to distribute your estate. This is the most important step — the system
        enforces 100% coverage so nothing is ever left unaccounted for.
      </p>

      {/* Mode selector */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <ModeCard
          active={mode === "percentage"}
          onClick={() => setMode("percentage")}
          icon={Percent}
          title="By Percentage"
          subtitle="Split the whole estate in % shares. Total must equal exactly 100%."
          testId="dist-mode-percentage"
        />
        <ModeCard
          active={mode === "asset_wise"}
          onClick={() => setMode("asset_wise")}
          icon={Package}
          title="Asset-wise"
          subtitle="Assign each individual asset to one or more beneficiaries."
          testId="dist-mode-asset-wise"
        />
      </div>

      {mode === "percentage" && (
        <PercentageMode
          family={family}
          percentages={distribution.percentages}
          setPercentages={(rows) =>
            setDistribution({ ...distribution, percentages: rows })
          }
        />
      )}

      {mode === "asset_wise" && (
        <AssetWiseMode
          assets={assets}
          family={family}
          assignments={distribution.asset_assignments}
          setAssignments={(rows) =>
            setDistribution({ ...distribution, asset_assignments: rows })
          }
        />
      )}
    </div>
  );
}

function ModeCard({ active, onClick, icon: Icon, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-5 text-left transition-all hover-lift ${
        active
          ? "border-gold bg-[#FBF7EE]"
          : "border-[#0A1128]/10 bg-white hover:border-gold/40"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-md ${
            active ? "bg-navy text-gold" : "bg-[#F9FAFB] text-navy"
          }`}
        >
          <Icon size={18} />
        </div>
        <div className="font-serif text-lg text-navy">{title}</div>
      </div>
      <p className="mt-3 text-sm text-[#4A5568]">{subtitle}</p>
    </button>
  );
}

/* ---------- PERCENTAGE MODE ---------- */
function PercentageMode({ family, percentages, setPercentages }) {
  const [picker, setPicker] = useState("");
  const total = percentageTotal(percentages);
  const remaining = 100 - total;
  const status =
    Math.abs(total - 100) < 0.001
      ? { tone: "success", label: "Total = 100% ✓" }
      : total > 100
      ? { tone: "error", label: `Over-allocated by ${(total - 100).toFixed(2)}%` }
      : { tone: "warn", label: `${remaining.toFixed(2)}% remaining — please assign.` };

  const add = () =>
    setPercentages([
      ...percentages,
      { id: uid(), beneficiary_name: "", beneficiary_relationship: "", share: "", is_minor: false, guardian_name: "", guardian_relation: "" },
    ]);

  const addFromFamily = (name) => {
    if (!name) return;
    const member = family.find((f) => f.name === name);
    if (!member) return;
    if (percentages.some((p) => p.beneficiary_name === member.name)) return;
    setPercentages([
      ...percentages,
      {
        id: uid(),
        beneficiary_name: member.name,
        beneficiary_relationship: member.relationship,
        share: "",
        is_minor: Number(member.age) < 18,
        guardian_name: "",
        guardian_relation: "",
      },
    ]);
    setPicker("");
  };

  const remove = (i) =>
    setPercentages(percentages.filter((_, idx) => idx !== i));
  const update = (i, k, v) =>
    setPercentages(
      percentages.map((row, idx) => (idx === i ? { ...row, [k]: v } : row))
    );

  const distributeEven = () => {
    if (percentages.length === 0) return;
    const share = Math.floor((100 / percentages.length) * 100) / 100;
    const rows = percentages.map((p, i) => ({
      ...p,
      share: i === percentages.length - 1
        ? (100 - share * (percentages.length - 1)).toFixed(2)
        : share.toFixed(2),
    }));
    setPercentages(rows);
  };

  return (
    <div className="mt-6 space-y-4" data-testid="percentage-mode-panel">
      <StatusBanner status={status} remaining={remaining} total={total} />

      <div className="space-y-3">
        {percentages.map((p, i) => (
          <Card key={p.id} className="border-[#0A1128]/10" data-testid={`pct-row-${i}`}>
            <CardContent className="grid gap-4 p-5 sm:grid-cols-[1fr_180px_140px_auto]">
              <div>
                <Label>Beneficiary name *</Label>
                <Input
                  className="mt-2"
                  value={p.beneficiary_name}
                  onChange={(e) => update(i, "beneficiary_name", e.target.value)}
                  placeholder="e.g. Riya Sharma"
                  data-testid={`pct-name-${i}`}
                />
              </div>
              <div>
                <Label>Relationship</Label>
                <Input
                  className="mt-2"
                  value={p.beneficiary_relationship}
                  onChange={(e) => update(i, "beneficiary_relationship", e.target.value)}
                  placeholder="Daughter / Son / Spouse"
                  data-testid={`pct-rel-${i}`}
                />
              </div>
              <div>
                <Label>Share (%) *</Label>
                <div className="relative mt-2">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={p.share}
                    onChange={(e) => update(i, "share", e.target.value)}
                    className="pr-8"
                    data-testid={`pct-share-${i}`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5568]">%</span>
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2 border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => remove(i)}
                  data-testid={`pct-remove-${i}`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
            <div className="border-t border-[#0A1128]/10 bg-[#F9FAFB]/60 px-5 py-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-navy">
                <Checkbox
                  checked={!!p.is_minor}
                  onCheckedChange={(v) => update(i, "is_minor", !!v)}
                  className="border-navy data-[state=checked]:bg-navy data-[state=checked]:text-gold"
                  data-testid={`pct-minor-toggle-${i}`}
                />
                Is this beneficiary a minor (under 18)?
              </label>
              {p.is_minor && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid={`pct-minor-fields-${i}`}>
                  <div>
                    <Label className="text-xs">Guardian name *</Label>
                    <Input
                      className="mt-1.5"
                      value={p.guardian_name || ""}
                      onChange={(e) => update(i, "guardian_name", e.target.value)}
                      placeholder="Who will manage their share?"
                      data-testid={`pct-guardian-name-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Guardian relation *</Label>
                    <Input
                      className="mt-1.5"
                      value={p.guardian_relation || ""}
                      onChange={(e) => update(i, "guardian_relation", e.target.value)}
                      placeholder="e.g. Aunt, Uncle, Grandparent"
                      data-testid={`pct-guardian-rel-${i}`}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={add}
          className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
          data-testid="pct-add-btn"
        >
          <Plus size={16} className="mr-2" /> Add beneficiary
        </Button>

        <div className="flex items-center gap-2">
          <select
            className="h-10 rounded-md border border-[#0A1128]/15 bg-white px-3 text-sm"
            value={picker}
            onChange={(e) => addFromFamily(e.target.value)}
            data-testid="pct-family-picker"
          >
            <option value="">+ Quick-add from family list…</option>
            {family
              .filter((f) => f.name && !percentages.some((p) => p.beneficiary_name === f.name))
              .map((f) => (
                <option key={f.id} value={f.name}>
                  {f.name} ({f.relationship})
                </option>
              ))}
          </select>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={distributeEven}
          className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
          disabled={percentages.length === 0}
          data-testid="pct-even-btn"
        >
          Distribute equally
        </Button>
      </div>
    </div>
  );
}

function StatusBanner({ status, remaining, total }) {
  const palette = {
    success: {
      border: "border-emerald-300",
      bg: "bg-emerald-50",
      text: "text-emerald-800",
      icon: CheckCircle2,
    },
    warn: {
      border: "border-gold/50",
      bg: "bg-[#FBF7EE]",
      text: "text-navy",
      icon: AlertTriangle,
    },
    error: {
      border: "border-red-300",
      bg: "bg-red-50",
      text: "text-red-700",
      icon: AlertTriangle,
    },
  }[status.tone];
  const Icon = palette.icon;

  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-md border ${palette.border} ${palette.bg} p-4`}
      data-testid="pct-status-banner"
    >
      <div className={`flex items-center gap-3 text-sm font-medium ${palette.text}`}>
        <Icon size={18} />
        {status.label}
      </div>
      <div className={`font-serif text-2xl font-semibold ${palette.text}`} data-testid="pct-total-display">
        {total.toFixed(2)}%
      </div>
    </div>
  );
}

/* ---------- ASSET-WISE MODE ---------- */
function AssetWiseMode({ assets, family, assignments, setAssignments }) {
  // sync assignments with current assets (add missing, drop removed)
  const assignmentByAssetId = new Map(
    assignments.map((a) => [a.asset_id, a])
  );
  const synced = assets.map((asset) => {
    const existing = assignmentByAssetId.get(asset.id);
    return (
      existing || {
        asset_id: asset.id,
        asset_description: asset.description || asset.type,
        beneficiaries: [],
        notes: "",
      }
    );
  });
  if (synced.length !== assignments.length) {
    setAssignments(synced);
  }

  const updateBeneficiaries = (assetId, names) => {
    setAssignments(
      synced.map((row) =>
        row.asset_id === assetId ? { ...row, beneficiaries: names } : row
      )
    );
  };

  const updateNotes = (assetId, notes) => {
    setAssignments(
      synced.map((row) =>
        row.asset_id === assetId ? { ...row, notes } : row
      )
    );
  };

  const [customName, setCustomName] = useState({});

  const addCustomBeneficiary = (assetId) => {
    const v = (customName[assetId] || "").trim();
    if (!v) return;
    const row = synced.find((r) => r.asset_id === assetId);
    if (row.beneficiaries.includes(v)) return;
    updateBeneficiaries(assetId, [...row.beneficiaries, v]);
    setCustomName((p) => ({ ...p, [assetId]: "" }));
  };

  const familyNames = family.map((f) => f.name).filter(Boolean);
  const unassignedCount = synced.filter((r) => r.beneficiaries.length === 0).length;

  return (
    <div className="mt-6 space-y-4" data-testid="asset-wise-panel">
      <div
        className={`flex items-center justify-between rounded-md border p-4 text-sm font-medium ${
          unassignedCount === 0
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-gold/50 bg-[#FBF7EE] text-navy"
        }`}
        data-testid="asset-wise-status"
      >
        <div className="flex items-center gap-3">
          {unassignedCount === 0 ? (
            <CheckCircle2 size={18} />
          ) : (
            <AlertTriangle size={18} />
          )}
          {unassignedCount === 0
            ? `All ${synced.length} assets assigned ✓`
            : `${unassignedCount} of ${synced.length} assets still unassigned.`}
        </div>
      </div>

      {synced.map((row, i) => {
        const asset = assets.find((a) => a.id === row.asset_id);
        const unassigned = row.beneficiaries.length === 0;
        return (
          <Card
            key={row.asset_id}
            className={`${unassigned ? "border-red-300" : "border-[#0A1128]/10"}`}
            data-testid={`asset-wise-row-${i}`}
          >
            <CardContent className="p-5">
              <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                    {asset?.type || "Asset"}
                  </div>
                  <div className="mt-1 font-serif text-lg text-navy">
                    {row.asset_description}
                  </div>
                  {asset?.value && (
                    <div className="text-xs text-[#4A5568]">Value: {asset.value}</div>
                  )}
                </div>
                {unassigned ? (
                  <span className="rounded-full bg-red-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-red-700">
                    Unassigned
                  </span>
                ) : (
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                    {row.beneficiaries.length} {row.beneficiaries.length === 1 ? "beneficiary" : "beneficiaries"}
                  </span>
                )}
              </div>

              <div className="mt-4">
                <Label>Assign to (from family) *</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {familyNames.length === 0 && (
                    <span className="text-xs text-[#4A5568]">
                      No family members added yet — go back to Step 2.
                    </span>
                  )}
                  {familyNames.map((name) => {
                    const checked = row.beneficiaries.includes(name);
                    return (
                      <label
                        key={name}
                        className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          checked
                            ? "border-navy bg-navy text-white"
                            : "border-[#0A1128]/15 bg-white text-navy hover:border-gold"
                        }`}
                        data-testid={`asset-wise-chip-${i}-${name}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = v
                              ? [...row.beneficiaries, name]
                              : row.beneficiaries.filter((x) => x !== name);
                            updateBeneficiaries(row.asset_id, next);
                          }}
                          className="h-3.5 w-3.5 border-current"
                        />
                        {name}
                      </label>
                    );
                  })}
                </div>
              </div>

              {row.beneficiaries.filter((n) => !familyNames.includes(n)).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {row.beneficiaries
                    .filter((n) => !familyNames.includes(n))
                    .map((n) => (
                      <span
                        key={n}
                        className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-[#FBF7EE] px-3 py-1 text-sm text-navy"
                      >
                        {n}
                        <button
                          type="button"
                          onClick={() =>
                            updateBeneficiaries(
                              row.asset_id,
                              row.beneficiaries.filter((x) => x !== n)
                            )
                          }
                          className="text-navy/60 hover:text-red-600"
                          aria-label={`Remove ${n}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    ))}
                </div>
              )}

              <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
                <Input
                  value={customName[row.asset_id] || ""}
                  onChange={(e) =>
                    setCustomName((p) => ({ ...p, [row.asset_id]: e.target.value }))
                  }
                  placeholder="Or add a beneficiary not in the family list (e.g. charity, friend)"
                  data-testid={`asset-wise-custom-input-${i}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addCustomBeneficiary(row.asset_id)}
                  className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
                  data-testid={`asset-wise-custom-add-${i}`}
                >
                  <Plus size={16} className="mr-2" /> Add
                </Button>
              </div>

              <div className="mt-4">
                <Label>Notes (optional)</Label>
                <Input
                  className="mt-2"
                  value={row.notes}
                  onChange={(e) => updateNotes(row.asset_id, e.target.value)}
                  placeholder="e.g. 'to be held in trust until child turns 21'"
                  data-testid={`asset-wise-notes-${i}`}
                />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
